import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { LocalStorageService } from '@sondermind/local-storage';

import { IFeatureFlagService } from '../models/feature-flag.service.interface';
import { FEATURE_FLAG_CI_VALUES } from '../models/feature-flag-ci-values';
import { EvaluationContext, FeatureFlagVariant } from '../models/feature-flag-types';
import { FeatureFlagKey } from '../models/feature-flag-key.enum';
import { IEvaluationOptions } from '../models/evaluation-options.interface';

// Cypress tests use this key to store feature flag overrides in localStorage
const AMPLITUDE_FEATURE_FLAG_OVERRIDE_KEY = 'cypress_amplitude_ff_overrides';

/**
 * CI implementation of the feature flag service.
 * This service uses hardcoded values for feature flags during cypresstesting and CI builds.
 * It's designed to provide consistent, predictable behavior for automated tests.
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagCiService implements IFeatureFlagService {
  constructor(private localStorageService: LocalStorageService) {}

  anonymousId$(): Observable<string> {
    return of('b187833a-0d1d-4f3b-a969-1c3794c00ad6');
  }

  initialize$(): Observable<boolean> {
    return of(true);
  }

  evaluate$<TKey extends string = string, TPayload = unknown>(
    flagKey: FeatureFlagKey,
    defaultValue: TPayload,
    options?: IEvaluationOptions
  ): Observable<FeatureFlagVariant<TKey, TPayload> | null> {
    const value = this.getFlagValue(flagKey, defaultValue);
    return of({ key: flagKey, payload: value } as FeatureFlagVariant<TKey, TPayload>);
  }

  isEnabled$(flagKey: FeatureFlagKey, defaultValue?: boolean): Observable<boolean> {
    const value = this.getFlagValue(flagKey, defaultValue ?? false);
    // Ensure we always return a boolean
    const booleanValue = typeof value === 'boolean' ? value : defaultValue ?? false;
    return of(booleanValue);
  }

  getVariant$<TKey extends string = string, TPayload = unknown>(
    context: EvaluationContext,
    flagKey: string
  ): Observable<FeatureFlagVariant<TKey, TPayload> | null> {
    const value = this.getFlagValue(flagKey as FeatureFlagKey, null);
    if (value === null || value === undefined) {
      return of(null);
    }

    return of({ key: flagKey as TKey, payload: value } as FeatureFlagVariant<TKey, TPayload>);
  }

  trackExposure$(flagKey: FeatureFlagKey, variant: FeatureFlagVariant): Observable<void> {
    // No-op implementation for CI/testing - we don't need to track exposures
    return of(undefined);
  }

  /**
   * Gets the value for a feature flag, checking Cypress overrides first, then CI defaults
   */
  private getFlagValue<T>(flagKey: FeatureFlagKey, defaultValue: T): T {
    // Check for Cypress overrides first
    const cypressOverrides = this.getCypressOverrides();
    if (cypressOverrides && cypressOverrides[flagKey] !== undefined) {
      return cypressOverrides[flagKey] as T;
    }

    // Fall back to CI defaults
    const ciFlagValue = FEATURE_FLAG_CI_VALUES[flagKey];
    return ciFlagValue !== undefined ? (ciFlagValue as T) : defaultValue;
  }

  /**
   * Retrieves Cypress feature flag overrides from localStorage
   */
  private getCypressOverrides(): Record<string, unknown> | null {
    // Use LocalStorageService to safely get the stored overrides
    const storedOverrides = this.localStorageService.getItem(AMPLITUDE_FEATURE_FLAG_OVERRIDE_KEY);
    if (storedOverrides) {
      try {
        return JSON.parse(storedOverrides);
      } catch (error) {
        // If JSON parsing fails, return null
        return null;
      }
    }

    return null;
  }
}
